import { useEffect, useState } from "react"

export type TPermissionState = "granted" | "prompt" | "denied" | "unknown"

interface IPermissionStatus extends EventTarget {
  state: PermissionState
  onchange: (() => void) | null
}

/**
 * Tracks a `navigator.permissions` permission live and re-renders when the
 * user updates it from the browser UI. Returns "unknown" when the Permissions
 * API isn't supported (e.g., Safari ≤ 16 for `microphone`); callers should
 * fall through to a `getUserMedia` attempt in that case.
 */
export function usePermissionState(name: PermissionName): TPermissionState {
  const [state, setState] = useState<TPermissionState>("unknown")

  useEffect(() => {
    let cancelled = false
    let status: IPermissionStatus | null = null
    const handleChange = (): void => {
      if (!cancelled && status) setState(status.state as TPermissionState)
    }

    if (typeof navigator === "undefined" || !navigator.permissions?.query) {
      setState("unknown")
      return
    }

    navigator.permissions
      .query({ name })
      .then((result) => {
        if (cancelled) return
        status = result as unknown as IPermissionStatus
        setState(status.state as TPermissionState)
        status.addEventListener("change", handleChange)
      })
      .catch(() => {
        if (!cancelled) setState("unknown")
      })

    return () => {
      cancelled = true
      if (status) status.removeEventListener("change", handleChange)
    }
  }, [name])

  return state
}
